<template>
  <span
    class="tab-subtitle-hint"
    @touchstart.stop.prevent="toggleFromTouch"
    @click.stop="toggle"
  >
    <AppIcon
      ref="icon"
      name="question-circle"
      font-scale="1.4"
      weight="500"
      :fill="open"
      class="tab-subtitle-hint__icon"
      :class="{ 'tab-subtitle-hint__icon--active': open }"
    ></AppIcon>

    <div v-if="open"
         class="tab-subtitle-hint__bubble"
         :style="bubbleStyle"
         @click.stop>
      <span class="tab-subtitle-hint__arrow" :style="arrowStyle"></span>
      <span v-if="icon" class="tab-subtitle-hint__bubble-icon">
        <AppIcon :name="icon" font-scale="1.3" weight="550" />
      </span>
      <span v-if="text" class="tab-subtitle-hint__bubble-text">{{ text }}</span>
    </div>
  </span>
</template>

<script>
import { EventBus } from '@/handler/event-bus'

const BUBBLE_WIDTH = 260
const VIEWPORT_MARGIN = 10
const ARROW_MARGIN = 16

export default {
  name: 'TabSubtitleHint',
  props: {
    text: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      open: false,
      bubbleStyle: {},
      arrowStyle: {},
      ignoreSyntheticClick: false,
      syntheticClickTimeout: null,
    }
  },
  created() {
    // Solo una burbuja abierta a la vez: si se toca el ícono de otra pestaña,
    // ese click hace stopPropagation y nunca llega al listener de "afuera"
    // de esta instancia, así que sin este evento quedarían las dos abiertas.
    EventBus.$on('TabSubtitleHint:open', this.closeIfNotSelf)
  },
  beforeDestroy() {
    EventBus.$off('TabSubtitleHint:open', this.closeIfNotSelf)
    this.unbindOutsideHandlers()
    window.clearTimeout(this.syntheticClickTimeout)
  },
  methods: {
    toggle() {
      // Luego de touchstart algunos navegadores disparan un click sintético.
      // La burbuja ya se abrió con el toque, por lo que ese click no debe
      // alternarla nuevamente ni retrasar su visualización.
      if (this.ignoreSyntheticClick) {
        this.ignoreSyntheticClick = false
        return
      }
      if (this.open) {
        this.close()
      } else {
        this.openBubble()
      }
    },
    toggleFromTouch() {
      this.ignoreSyntheticClick = true
      window.clearTimeout(this.syntheticClickTimeout)
      this.syntheticClickTimeout = window.setTimeout(() => {
        this.ignoreSyntheticClick = false
      }, 500)
      if (this.open) {
        this.close()
      } else {
        this.openBubble()
      }
    },
    openBubble() {
      this.open = true
      EventBus.$emit('TabSubtitleHint:open', this._uid)
      this.$nextTick(() => {
        this.positionBubble()
        this.bindOutsideHandlers()
      })
    },
    closeIfNotSelf(uid) {
      if (uid !== this._uid) this.close()
    },
    close() {
      if (!this.open) return
      this.open = false
      this.unbindOutsideHandlers()
    },
    bindOutsideHandlers() {
      document.addEventListener('click', this.close)
      // Reposiciona en vez de cerrar: la barra de tabs es scrolleable y el
      // propio tap contra el ícono dispara un scroll de reajuste, así que
      // cerrar acá hacía parpadear la burbuja apenas se abría.
      window.addEventListener('scroll', this.positionBubble, true)
      window.addEventListener('resize', this.positionBubble)
    },
    unbindOutsideHandlers() {
      document.removeEventListener('click', this.close)
      window.removeEventListener('scroll', this.positionBubble, true)
      window.removeEventListener('resize', this.positionBubble)
    },
    // La ubica debajo del ícono y la corre para adentro si se saldría de la
    // pantalla (tabs cerca del borde derecho en mobile). La flechita se
    // reposiciona con el mismo cálculo para seguir apuntando al ícono aunque
    // la burbuja se haya corrido.
    positionBubble() {
      const iconEl = this.$refs.icon.$el
      const rect = iconEl.getBoundingClientRect()
      const iconCenter = rect.left + rect.width / 2

      let left = iconCenter - BUBBLE_WIDTH / 2
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - BUBBLE_WIDTH - VIEWPORT_MARGIN))

      const arrowLeft = Math.max(ARROW_MARGIN, Math.min(iconCenter - left, BUBBLE_WIDTH - ARROW_MARGIN))

      this.bubbleStyle = {
        top: rect.bottom + 8 + 'px',
        left: left + 'px',
        width: BUBBLE_WIDTH + 'px',
      }
      this.arrowStyle = {
        left: arrowLeft + 'px',
      }
    },
  },
}
</script>

<style scoped>
.tab-subtitle-hint {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  cursor: pointer;
}
.tab-subtitle-hint__icon {
  color: var(--app-color-text-muted);
}
.tab-subtitle-hint__icon--active {
  color: var(--app-color-primary);
}
.tab-subtitle-hint__bubble {
  position: fixed;
  z-index: 1060;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--app-color-border);
  border-radius: 10px;
  background-color: var(--app-color-surface);
  box-shadow: var(--app-shadow-md);
  color: var(--app-color-text);
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  text-transform: none;
  letter-spacing: normal;
  white-space: normal;
}
.tab-subtitle-hint__bubble-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.tab-subtitle-hint__bubble-text {
  flex: 1 1 auto;
  min-width: 0;
}
.tab-subtitle-hint__arrow {
  position: absolute;
  top: -6px;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  border-top: 1px solid var(--app-color-border);
  border-left: 1px solid var(--app-color-border);
  background-color: var(--app-color-surface);
  transform: rotate(45deg);
}
</style>
